import React, { ForwardedRef, forwardRef, ReactNode } from 'react';
import clsx from 'clsx';
import {
  useCheckboxGroup,
  AriaCheckboxGroupProps,
} from 'react-aria/useCheckboxGroup';
import { mergeProps } from 'react-aria/mergeProps';
import { useCheckboxGroupState } from 'react-stately/useCheckboxGroupState';
import styles from '@danske/sapphire-css/components/fieldGroup/fieldGroup.module.css';
import { CheckboxGroupStateContext } from 'react-aria-components';

import { CheckboxGroupContext } from './CheckboxGroupContext';
import { Field } from '../../Field';
import { Label } from '../../Label';
import { SapphireStyleProps, useThemeCheck } from '../../utils';
import {
  BaseComponentKeys,
  ValidationIsRequiredKey,
  ValueBasePropsKeys,
  InputBaseKeys,
  FocusEventsKeys,
  GlobalDomAttributes,
} from '../../utils/types';

export interface CheckboxGroupProps
  extends Pick<
      AriaCheckboxGroupProps,
      | BaseComponentKeys
      | ValueBasePropsKeys
      | InputBaseKeys
      | 'name'
      | 'label'
      | ValidationIsRequiredKey
      | FocusEventsKeys
    >,
    GlobalDomAttributes,
    SapphireStyleProps {
  /**
   * The layout of the checkbox items
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * A HelpButton to render next to the label
   */
  contextualHelp?: React.ReactNode;

  /**
   * {@Link Checkbox} elements to be rendered within the group.
   */
  children: React.ReactNode;

  /**
   * If set, the checkbox group becomes invalid with this associated error message.
   */
  error?: ReactNode;

  /**
   * A note to show below the checkbox group.
   * If the checkbox group has an error message, this note will be replaced by that.
   * Use {@Link Field.Note} to render the note.
   */
  note?: ReactNode;

  /**
   * @default 'lg'
   */
  size?: 'lg' | 'md';
  /**
   * To visually indicate if this field is required or optional.
   * @default false
   */
  necessityIndicator?: boolean;

  /**
   * Places the label either above (default) or on the side of the control.
   * @default 'above'
   */
  labelPlacement?: 'side' | 'above';
}

export const CheckboxGroup = forwardRef(function CheckboxGroup(
  props: CheckboxGroupProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const {
    orientation = 'vertical',
    contextualHelp,
    error,
    note,
    children,
    label,
    size = 'lg',
    necessityIndicator = false,
    labelPlacement = 'above',
    isDisabled,
    ...otherProps
  } = props;

  const state = useCheckboxGroupState(props);
  const { labelProps, groupProps, errorMessageProps, descriptionProps } =
    useCheckboxGroup({ ...props, label, isInvalid: !!error }, state);

  return (
    <Field
      {...otherProps}
      size={size}
      labelPlacement={labelPlacement}
      labelVerticalAlignment="top"
      noDefaultWidth
    >
      <Field.Context
        descriptionProps={error ? errorMessageProps : descriptionProps}
      >
        {(label || contextualHelp) && (
          <Field.Label>
            <Label
              {...labelProps}
              size={size}
              necessityIndicator={
                props.isRequired && necessityIndicator
                  ? 'required'
                  : !props.isRequired && necessityIndicator
                  ? 'optional'
                  : undefined
              }
              contextualHelp={contextualHelp}
            >
              {label}
            </Label>
          </Field.Label>
        )}
        <Field.Control>
          <div
            className={clsx(
              styles['sapphire-field-group'],
              styles[`sapphire-field-group--${orientation}` as const],
              {
                [styles['sapphire-field-group--md']]: size === 'md',
              }
            )}
            // 'aria-invalid' prop is not returned via groupProps when the validationState is passed to react-aria's useCheckboxGroup hook.
            //  Hence, passing 'aria-invalid' here.
            {...mergeProps(groupProps, { 'aria-invalid': error !== undefined })}
            ref={ref}
          >
            <CheckboxGroupStateContext.Provider value={state}>
              <CheckboxGroupContext.Provider value={{ size }}>
                {children}
              </CheckboxGroupContext.Provider>
            </CheckboxGroupStateContext.Provider>
          </div>
        </Field.Control>
        {(error || note) && (
          <Field.Footer>
            {error ? (
              <Field.Note variant="error">{error}</Field.Note>
            ) : note ? (
              note
            ) : null}
          </Field.Footer>
        )}
      </Field.Context>
    </Field>
  );
});
